import React, { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation } from "react-router-dom";

import EmptyState from "@components-shared/EmptyState";
import LoaderSpinner from "@components-shared/LoaderSpinner";
import Currency from "@components-shared/Currency";

import { getCurrentCulture, isUserAuthenticated } from "@utils";
import Number from "@components-shared/Number";
import ErrorBoundaryWithPropagation from "@components/boundary/ErrorBoundaryWithPropagation";
import { Button } from "@components/buttons";
import { TBetSlipTab } from "@data-types/bet-slip";
import { BetSlipOffer, BetSlipOfferSportData, BetSlipTypes } from "@gp/models";
import { MultiOffer } from "@gp/models/dist/types/bet-slip/response/BetSlipOffer";
import { BetSlipBetType, EventType, createMultiOfferId } from "@gp/slip";
import { NameProvider } from "@gp/utility";
import { useRootAppStore } from "@hooks";
import BetSlipStore from "@state/bet-slip/BetSlipStore";
import { SavedSlipInfoType } from "@state/bet-slip/SavedSlipStore";
import { useAddTipsFromUrl } from "@state/hooks/offer/useAddTipsFromUrl";
import { getUrlParamsAsObj, removeFromUrlParams } from "@state/module-root";
import { redirectToLogin } from "@utils/auth";
import { DefaultPath } from "@utils/common/defaultRoutePathsEnum";
import { WaitingPeriodPopupStateEnum } from "@utils/common/waitingPeriodPopupStateEnum";
import { getSlipTip, getSlipType } from "@utils/offer";
import classNames from "classnames";
import { observer } from "mobx-react";

import { BetSlipConfirmationModal, BetSlipTypeSelector, OneClickBet } from ".";
import BetWidgetTabSelector from "./BetWidgetTabSelector";
import NumberInput from "./NumberInput";
import { THEME_SETTINGS } from "@theme-settings";
import { Freebet } from "@api-types/bet-slip/Freebet";
import { Modal } from "@lib/modal";
import { DateTime } from "luxon";


const nameProvider = new NameProvider();

function TeamName(props: {
	name: string;
	gender?: 1;
	isInBetBuilder?: boolean;
}) {
	const t = useTranslation().t;

	return (
		<>
			{props.name}{" "}
			{props.gender === 1 ? <>({t("OFFER.GENDER.WOMEN")})</> : null}
		</>
	);
}

// @ts-expect-error TODO
function EventInfo(props) {
	const { resultData, matchTime, sportData } = props;
	const { t } = useTranslation();

	if (!resultData || !matchTime) {
		return null;
	}

	const homeHalfTimeScore =
		resultData.halfTimeScore && resultData.halfTimeScore.home
			? resultData.halfTimeScore.home
			: 0;
	const awayHalfTimeScore =
		resultData.halfTimeScore && resultData.halfTimeScore.away
			? resultData.halfTimeScore.away
			: 0;

	let periods = {
		periodNumber: [],
		home: [],
		away: [],
	};
	if (resultData.scorePerPeriod) {
		// @ts-expect-error
		periods = resultData.scorePerPeriod.reduce((acc, item) => {
			var periodItem = {
				periodNumber: item.periodNumber,
				home: item.home || 0,
				away: item.away || 0,
			};

			acc.push(periodItem);

			return acc;
		}, []);
	}

	/**
	 * @deprecated use from @offer/helpers
	 */
	const formatGameScore = (input: string) =>
		input.startsWith("g") ? input.substr(1) : input.toUpperCase();

	return (
		<>
			<div className="slip__event__details">
				<div className="slip__event__time">{matchTime.displayTime}</div>
				<div className="slip__event__separator">|</div>

				{/* CURRENT SCORE */}
			{resultData.currentScore && sportData.sportAbrv == "cricket" ? (
					resultData?.scorePerPeriod.length > 1 ? (
						<div className="slip__event__result">
							<>
								{`${resultData?.scorePerPeriod[0].home || 0}/${resultData?.scorePerPeriod[1].home || 0}`}{" "}
								:{" "}
								{`${resultData?.scorePerPeriod[0].away || 0}/${resultData?.scorePerPeriod[1].away || 0}`}
							</>
						</div>
					) : (
						<div className="slip__event__result">
							<>
								{resultData?.scorePerPeriod[0].home || 0} :{" "}
								{resultData?.scorePerPeriod[0].away || 0}
							</>
						</div>
					)
				) : (
					<div className="slip__event__result">
						<>
							{resultData?.currentScore?.home || 0} :{" "}
							{resultData?.currentScore?.away || 0}
						</>
					</div>
				)}

				{/* HT - HALF TIME SCORE */}
				{resultData.halfTimeScore && (
					<div
						className="slip__event__stats slip__event__ht"
						title={t("EVENT.DETAILS.HT")}
					>
						<>
							({homeHalfTimeScore || 0}:{awayHalfTimeScore || 0})
						</>
					</div>
				)}

				{/* SCORE PER PERIOD */}
				{resultData.scorePerPeriod &&
				!resultData.halfTimeScore &&
				sportData.sportAbrv != "snooker" ? (
					<div className="slip__event__stats slip__event__score">
						<>
							({/* @ts-expect-error */}
							{periods.map((period, idx) => {
								return (
									<div
										key={idx}
										className="slip__event__period"
									>
										<>
											P{period.periodNumber} {period.home}
											:{period.away}
										</>
									</div>
								);
							})}
							)
						</>
					</div>
				) : null}

				{/* OT - OVERTIME SCORE */}
				{resultData.overtimeScore && (
					<div
						className="slip__event__stats slip__event__ot"
						title={t("EVENT.DETAILS.OT_TITLE")}
					>
						({resultData.overtimeScore.home || 0} :{" "}
						{resultData.overtimeScore.away || 0})
					</div>
				)}

				{/* TBS - TIE BREAKER SCORE */}
				{resultData.tiebreakScore && (
					<div
						className="slip__event__stats slip__event__tbs"
						title={t("EVENT.DETAILS.TBS_TITLE")}
					>
						({resultData.tiebreakScore.home || 0} :{" "}
						{resultData.tiebreakScore.away || 0})
					</div>
				)}

				{/* GAME SCORE - TENNIS */}
				{resultData.currentGameScore && !resultData.tiebreakScore && (
					<div
						className="slip__event__stats slip__event__game"
						title={t("EVENT.DETAILS.GAME_TITLE")}
					>
						(
						{formatGameScore(
							resultData.currentGameScore.home || "0"
						)}{" "}
						:{" "}
						{formatGameScore(
							resultData.currentGameScore.away || "0"
						)}
						)
					</div>
				)}
			</div>
		</>
	);
}

export default observer(function BetSlip() {
	const rootStore = useRootAppStore();
	const betSlipStore = rootStore.betSlipStore;

	useAddTipsFromUrl();

	return (
		<>
			<ErrorBoundaryWithPropagation componentName={"Desktop/BetSlip.tsx"}>
				<div className="slip">
					<BetWidgetTabSelector store={betSlipStore} />
					<BetSlipTab store={betSlipStore} />
				</div>
			</ErrorBoundaryWithPropagation>
		</>
	);
});

const BetSlipTab = observer(function BetSlipTab(props: {
	store: BetSlipStore;
}) {
	if (props.store.activeBetTab != TBetSlipTab.BetSlip) {
		return null;
	}
	return (
		<>
			<ErrorBoundaryWithPropagation componentName={"Desktop/BetSlip.tsx"}>
				<LoaderSpinner
					contained
					isLoading={
						props.store.isLoading &&
						!props.store.isCountdownVisible &&
						!props.store.isOneClickBetActive
					}
					className="slip__loader"
				/>
				<SlipBody store={props.store} />
				<BetSlipOneClickBetClear store={props.store} />
				<BetSlipReset store={props.store} />
			</ErrorBoundaryWithPropagation>
		</>
	);
});

const BetSlipOneClickBetClear = observer(
	function BetSlipOneClickBetClear(props: { store: BetSlipStore }) {
		const { showOneClickBetClearSlipModal } = props.store;

		if (!showOneClickBetClearSlipModal) {
			return null;
		}

		return (
			<BetSlipConfirmationModal
				headerText="LIVE.INFO"
				bodyText="BET_SLIP.ONE_CLICK_BET_CLEAR_CONFIRMATION"
				onAccept={props.store.acceptSlipClearAndSetToOneClick}
				onReject={props.store.onCloseOneClickBetChangeModal}
			/>
		);
	}
);

const BetSlipReset = observer(function BetSlipReset(props: {
	store: BetSlipStore;
}) {
	if (!props.store.showResetModal) {
		return null;
	}

	return (
		<BetSlipConfirmationModal
			headerText="LIVE.INFO"
			bodyText="BET_SLIP.CLEAR_BET_SLIP_CONFIRMATION"
			onAccept={props.store.acceptBetSlipReset}
			onReject={props.store.cancelBetSlipReset}
		/>
	);
});

const SlipBody = observer(function SlipBody(props: { store: BetSlipStore }) {
	return (
		<>
			<BetSlipTypeSelector store={props.store} />
			<div className="slip__body">
				<BetSlipBody store={props.store} />
			</div>
		</>
	);
});

const BetSlipBody = observer(function BetSlipBody(props: {
	store: BetSlipStore;
}) {
	const { t } = useTranslation();

	const params = getUrlParamsAsObj<{ isOneClickBet: boolean }>();

	useEffect(() => {
		if (params.isOneClickBet) {
			props.store.changeBetSlipBetType(BetSlipBetType.OneClickBet);
			removeFromUrlParams(["isOneClickBet"]);
		}

	}, []);

	const emptyStateStyle = {
		wrapperStyle: "slip__body is-empty",
		title: (
			<div className="u-type--med u-type--wgt--medium u-type--center">
				{t("BET_SLIP.ERRORS.NO_BETS_SELECTED")}
			</div>
		),
		iconStyle:
			"u-icon u-icon--huge u-icon--empty--slip u-align--h--center u-mar--top--xsml u-mar--bottom--big",
	};

	if (props.store.betSlipBetType === BetSlipBetType.OneClickBet) {
		return <OneClickBet {...props} />;
	}

	if (props.store.isSlipEmpty) {
		return (
			<>
				{/* @ts-expect-error */}
				<EmptyState styles={emptyStateStyle}>
					{t("BET_SLIP.CHOOSE_QUOTES")}
				</EmptyState>
			</>
		);
	}
	return (
		<>
			<LoaderSpinner
				isLoading={
					!props.store.isOneClickBetActive &&
					props.store.isCountdownVisible
				}
				isCountdown={{
					countdownValue: props.store.countdownCounter,
					isDesktop: true,
				}}
			/>
			<ErrorBoundaryWithPropagation
				componentName={"Desktop/side-widget/bet-slip.tsx/SlipEvents"}
			>
				<SlipEvents store={props.store} />
			</ErrorBoundaryWithPropagation>

			<SystemSection store={props.store} />

			<CheckOdds store={props.store} />

			<div className="slip__details">
				<ErrorBoundaryWithPropagation
					componentName={
						"Desktop/side-widget/bet-slip.tsx/RemoveInvalidOfferButton"
					}
				>
					<RemoveInvalidOfferButton />
				</ErrorBoundaryWithPropagation>

                <div className="slip__item">
                    <ErrorBoundaryWithPropagation
                        componentName={"Desktop/side-widget/bet-slip.tsx/FreebetSlipSubmit"}
                    >
                        <FreebetSlipSubmit />
                    </ErrorBoundaryWithPropagation>
                </div>
				<div className="slip__item">
					<EventCount store={props.store} />
				</div>
				<Combinations store={props.store} />

				{/* STAKES */}
				<ErrorBoundaryWithPropagation
					componentName={"Desktop/side-widget/bet-slip.tsx/Stakes"}
				>
					<Stakes store={props.store} />
				</ErrorBoundaryWithPropagation>

				{/* EO STAKES */}
				<HandlingFee store={props.store} />
				<SlipMaxCoefficient store={props.store} />
				<div className="slip__item">
					<div className="slip__details__row">
						<GainLabel store={props.store} />
						<Gain store={props.store} />
					</div>
				</div>
				<GainBonusPercentage store={props.store} />
				<GainTaxPercentage store={props.store} />
				<ErrorBoundaryWithPropagation
					componentName={"Desktop/side-widget/bet-slip.tsx/MaxGain"}
				>
					<MaxGain store={props.store} />
				</ErrorBoundaryWithPropagation>
				<ErrorBoundaryWithPropagation
					componentName={"Desktop/side-widget/bet-slip.tsx/Payment"}
				>
					<Payment store={props.store} />
				</ErrorBoundaryWithPropagation>
				<ErrorBoundaryWithPropagation
					componentName={
						"Desktop/side-widget/bet-slip.tsx/SaveSlipButton"
					}
				>
					<LoaderSpinner
						contained
						isLoading={
							props.store.savedSlipStore.loaderStore.isLoading
						}
						className="slip__loader"
					/>
					<SaveSlipButton store={props.store} />
				</ErrorBoundaryWithPropagation>
			</div>
			<SlipFooter store={props.store} />
		</>
	);
});

const SlipEvents = observer(function SlipEvents(props: {
	store: BetSlipStore;
}) {
	return (
		<>
			{Object.keys(props.store.displaySlipEventEntries).map(
				(eventId: string) => {
					const eventOffer =
						props.store.displaySlipEventEntries[eventId]; // array of offers for the same event
					const eventDetails = eventOffer[0].sportData;

					const eventHasErrors = eventOffer.some(
						(offer: BetSlipOffer) =>
							(offer.errors && offer.errors.length > 0) ||
							(offer.multiOffers &&
								offer.multiOffers.every(
									(multiOffer) =>
										multiOffer.errors &&
										multiOffer.errors.length > 0
								))
					);
					const isLive =
						// @ts-expect-error
						props.store.displaySlipEventEntries[eventId][0].isLive;

					if (
						eventOffer[0]?.multiOffers &&
						eventOffer[0].multiOffers.length >= 1
					) {
						return (
							<div
								key={eventId}
								className="slip__event betbuilder__event"
							>
								<BetBuilderEventDetails
									eventOffer={eventOffer[0]}
									eventId={eventId}
									isLive={isLive}
									eventDetails={eventDetails}
									eventHasErrors={eventHasErrors}
								/>
								<EventInfo
									resultData={eventOffer[0].eventResultData}
									matchTime={eventOffer[0].matchTime}
									sportData={eventOffer[0].sportData}
								/>

								<div
									key={eventOffer[0].id}
									className="slip__markets betbuilder__markets betbuilder--sml__markets"
								>
									{eventOffer[0].multiOffers.map(
										(offer, oIdx: number, array: any[]) => {
											const dotClasses = classNames(
												"betbuilder__dot betbuilder--sml__dot",
												{
													"is-only": array.length == 1,
													"is-last":
														array.length > 1 &&
														oIdx === array.length - 1,
												}
											);

											const lineClasses = classNames(
												"betbuilder__line betbuilder--sml__line",
												{
													"is-only": array.length == 1,
													"is-last":
														array.length > 1 &&
														oIdx === array.length - 1,
												}
											);

											return (
												<div
													key={offer.id}
													className="betbuilder__markets__row betbuilder--sml__markets__row"
												>
													<div className="betbuilder__track betbuilder--sml__track">
														{array.length >= 1 && (
															<div
																className={
																	dotClasses
																}
															/>
														)}

														{oIdx <
															array.length - 1 && (
															<div
																className={
																	lineClasses
																}
															/>
														)}
													</div>

													<BetBuilderEventOffer
														offer={offer}
														eventDetails={
															eventDetails
														}
														eventOffer={eventOffer[0]}
														store={props.store}
													/>
												</div>
											);
										}
									)}
								</div>
							</div>
						);
					}

					return (
						<div key={eventId} className="slip__event">
							<div className="slip__event__header">
								<div className="slip__event__title">
									<EventDetails
										eventId={eventId}
										isLive={isLive}
										eventDetails={eventDetails}
										eventHasErrors={eventHasErrors}
									/>

									<EventInfo
										resultData={
											eventOffer[0].eventResultData
										}
										matchTime={eventOffer[0].matchTime}
										sportData={eventOffer[0].sportData}
									/>
								</div>
								<BankButton
									eventId={eventId}
									store={props.store}
								/>
							</div>

							<div className="slip__markets">
								{!eventOffer[0]?.multiOffers &&
									eventOffer.map((offer) => (
										<EventOffer
											key={offer.id}
											offer={offer}
											eventDetails={eventDetails}
											store={props.store}
										/>
									))}
							</div>
						</div>
					);
				}
			)}
		</>
	);
});

const EventOffer = observer(function EventOffer(props: {
	offer: BetSlipStore["displaySlipEventEntries"][string][0];
	eventDetails: BetSlipOfferSportData;
	store: BetSlipStore;
}) {
	const isExcluded = props.offer.isExcluded;
	const isLocked =
		(props.offer.isEventLocked ||
			props.offer.isKeyLocked ||
			props.offer.isOfferLocked) &&
		props.offer.errors != null;
	const isStopped = props.offer.isDeleted;

	const specifiers = {
		...props.offer.specifier,
		competitor1: props.eventDetails.teamOneName,
		competitor2: props.eventDetails.teamTwoName,
	};

	const name = getSlipType(
		props.offer,
		props.store.betSlipState.lookupStore,
		specifiers
	);

	const tip = getSlipTip(
		props.offer,
		props.store.betSlipState.lookupStore,
		nameProvider
	);

	const slipMatchPickIconClasses = classNames(
		"slip__pick__icon u-icon u-icon--tny",
		{
			"u-icon--remove--danger": isStopped,
			"u-icon--lock u-color--sent--danger": isLocked,
			"u-icon--lock": isExcluded && isLocked,
			"is-hidden":
				!isStopped &&
				!props.offer.isEventLocked &&
				!props.offer.isKeyLocked &&
				!props.offer.isOfferLocked &&
				props.offer.errors == null,
		}
	);

	return (
		<div
			className={classNames("slip__pick", {
				"is-excluded": isExcluded,
				"is-locked": isLocked,
				"bet-stop": isStopped,
			})}
		>
			{props.store.currentSlipOffers.length > 1 || isExcluded ? (
				<button
					type="button"
					onClick={() =>
						props.store.toggleTipExclude(props.offer.bettingOfferId)
					}
					className="slip__btn--outlined slip__pick__exclude btn btn--square btn--square--xxsml btn--primary--inverted btn--animate-grow"
				>
					{isExcluded ? (
						<i className="u-icon u-icon--tny u-icon--check u-color--sent--muted" />
					) : (
						<i className="u-icon u-icon--tny u-icon--check u-color--sent--success" />
					)}
				</button>
			) : (
				<button
					type="button"
					className="slip__btn--outlined slip__pick__exclude btn btn--square btn--square--xxsml btn--primary--inverted is-disabled"
					disabled
				>
					{isExcluded ? (
						<i className="u-icon u-icon--tny u-icon--check u-color--sent--muted" />
					) : (
						<i className="u-icon u-icon--tny u-icon--check u-color--sent--success" />
					)}
				</button>
			)}

			<div
				className={classNames("slip__pick__type", {
					"is-excluded": isExcluded,
					"is-locked": isLocked,
				})}
			>
				<i className={slipMatchPickIconClasses} />

				{props.offer.sportData.eventType ===
				EventType.LongTerm ? null : (
					<React.Fragment>
						{name}
						{tip ? ":" : ""}
					</React.Fragment>
				)}

				<strong
					className={classNames("slip__pick__tip", {
						"is-excluded": isExcluded,
						"is-locked": isLocked,
					})}
				>
					{tip}
				</strong>
			</div>

			<div
				className={classNames("slip__pick__odd quote--slip", {
					desc: props.offer.indicator === -1,
					inc: props.offer.indicator === 1,
					"is-excluded": isExcluded,
					"is-locked": isLocked,
				})}
			>
				{props.offer.displayValue}
			</div>

			<RemoveEventOfferButton
				bettingOfferId={props.offer.bettingOfferId}
				store={props.store}
				isExcluded={props.offer.isExcluded}
			/>
		</div>
	);
});

const BetBuilderEventOffer = observer(function BetBuilderEventOffer(
	props: Omit<Parameters<typeof EventOffer>[0], "offer"> & {
		offer: MultiOffer;
		eventOffer: BetSlipStore["displaySlipEventEntries"][string][0];
	}
) {
	const slipMatchPickIconClasses = classNames(
		"slip__pick__icon betbuilder__pick__icon u-icon u-icon--tny",
		{
			"u-icon--remove--danger": props.offer.isDeleted,
			"u-icon--lock u-color--sent--danger":
				props.offer.isEventLocked ||
				props.offer.isKeyLocked ||
				(props.offer.isOfferLocked && props.offer.errors != null),
			"u-icon--lock":
				(props.offer.isKeyLocked || props.offer.isOfferLocked) &&
				props.offer.errors != null,
			"is-hidden":
				!props.offer.isDeleted &&
				!props.offer.isEventLocked &&
				!props.offer.isKeyLocked &&
				!props.offer.isOfferLocked &&
				props.offer.errors == null,
		}
	);

	const specifiers = {
		...props.offer.specifier,
		competitor1: props.eventDetails.teamOneName,
		competitor2: props.eventDetails.teamTwoName,
	};

	const name = getSlipType(
		props.offer,
		props.store.betSlipState.lookupStore,
		specifiers
	);

	const tip = getSlipTip(
		props.offer,
		props.store.betSlipState.lookupStore,
		nameProvider
	);

	return (
		<div
			className={classNames("slip__pick betbuilder__pick", {
				"is-excluded": props.offer.isExcluded,
				"is-locked":
					(props.offer.isEventLocked ||
						props.offer.isKeyLocked ||
						props.offer.isOfferLocked) &&
					props.offer.errors != null,
				"is-stopped": props.offer.isDeleted,
			})}
		>
			<div className="slip__pick__type">
				{props.offer.errors != null && (
					<i className={slipMatchPickIconClasses} />
				)}

				{props.offer.sportData.eventType ===
				EventType.LongTerm ? null : (
					<React.Fragment>
						{name}
						{tip ? ":" : ""}
					</React.Fragment>
				)}

				<strong
					className={classNames(
						"slip__pick__tip betbuilder__pick__tip"
					)}
				>
					{tip}
				</strong>
			</div>

			<RemoveBetBuilderOfferButton
				offer={props.offer}
				store={props.store}
			/>
		</div>
	);
});
const BetBuilderEventDetails = observer(function BetBuilderEventDetails(
	props: Parameters<typeof EventDetails>[0]
) {
	const { eventDetails, eventHasErrors, isLive, eventOffer } = props;
	const location = useLocation();
	const rootStore = useRootAppStore();
	const betBuilderEventId = eventOffer.eventId;

	const isExcluded = props.eventOffer.isExcluded;
	const isLocked = props.eventHasErrors;
	const isStopped =
		props.eventOffer.multiOffers &&
		props.eventOffer.multiOffers.every(
			(multiOffer: BetSlipOffer) => multiOffer.isDeleted
		);

	const slipMatchPickIconClasses = classNames(
		"slip__event__icon betbuilder__event__icon u-icon u-icon--sml",
		{
			"u-icon--remove u-color--sent--danger": isStopped,
			"u-icon--bb--active":
				(!isLocked && !props.eventOffer.multiOffers) ||
				(!props.eventOffer.multiOffers.every(
					(multiOffer: BetSlipOffer) => multiOffer.isDeleted
				) &&
					!isExcluded),
			"u-icon--bb--excluded": isExcluded,
		}
	);

	/* Take split of eventId because bet builder offer is prefixed with multiBet */
	if (eventDetails.eventType !== EventType.Normal) {
		return (
			<LinkEventHOC
				eventId={betBuilderEventId}
				isLive={isLive}
				className={classNames("slip__event__competitors u-anchor", {
					"is-excluded": isExcluded,
					"is-locked": isLocked,
					"is-stopped": isStopped,
				})}
			>
				{eventDetails.eventName}
			</LinkEventHOC>
		);
	}

	return (
		<div
			className={classNames("betbuilder__event__title u-anchor", {
				// "slip__event__header betbuilder__event__header": eventDetails.eventType === EventType.Normal,
				// "slip__pick": eventDetails.eventType !== EventType.Normal,
				"is-excluded": isExcluded,
				"is-locked": isLocked,
				"is-stopped": isStopped,
			})}
		>
			{(rootStore.betSlipStore.currentSlipOffers.length > 1 ||
				eventOffer.isExcluded) && (
				<button
					type="button"
					onClick={(e) => {
						e.preventDefault();
						e.stopPropagation();
						rootStore.betSlipStore.toggleTipExclude(
							eventOffer.bettingOfferId
						);
					}}
					className="slip__btn--outlined slip__pick__exclude btn btn--square btn--square--xxsml btn--primary--inverted btn--animate-grow"
				>
					{isExcluded ? (
						<i className="u-icon u-icon--tny u-icon--check u-color--sent--muted" />
					) : (
						<i className="u-icon u-icon--tny u-icon--check u-color--sent--success" />
					)}
				</button>
			)}

			<LinkEventHOC
				eventId={betBuilderEventId}
				isLive={isLive}
				className={classNames(
					"slip__event__competitors betbuilder__event__competitors u-anchor",
					{
						"u-color--sent--danger":
							eventOffer?.errors?.length > 0 ||
							eventOffer.multiOffers.some(
								(offer: BetSlipOffer) =>
									offer.errors && offer.errors.length > 0
							),
						"is-disabled":
							location.pathname.includes(betBuilderEventId) ||
							eventHasErrors,
					}
				)}
			>
				<>
					<i className={slipMatchPickIconClasses} />
					<TeamName
						name={eventDetails.teamOneName}
						gender={eventDetails.teamOneGender}
						isInBetBuilder
					/>{" "}
					-{" "}
					<TeamName
						name={eventDetails.teamTwoName}
						gender={eventDetails.teamTwoGender}
						isInBetBuilder
					/>
				</>
			</LinkEventHOC>

			<div
				className={classNames(
					"slip__pick__odd betbuilder__pick__odd quote--slip",
					{
						desc: eventOffer.indicator === -1,
						inc: eventOffer.indicator === 1,
						"is-excluded": isExcluded,
						"is-locked": isLocked,
						"is-stopped": isStopped,
						"u-color--sent--danger": eventHasErrors,
					}
				)}
			>
				{eventOffer.displayValue !== 0 &&
				eventOffer.displayValue != null
					? eventOffer.displayValue
					: "-"}
			</div>

			<RemoveBetBuilderEventButton
				eventId={eventOffer.eventId}
				store={rootStore.betSlipStore}
				isExcluded={eventOffer.isExcluded}
			/>
		</div>
	);
});

// @ts-expect-error TODO
const EventDetails = observer(function EventDetails(props) {
	const { eventDetails, eventHasErrors, eventId, isLive } = props;
	const location = useLocation();

	const teamClasses = classNames("slip__event__competitors", {
		"u-color--sent--danger": eventHasErrors,
	});

	if (eventHasErrors || location.pathname.includes(eventId)) {
		if (eventDetails.eventType !== EventType.Normal) {
			return (
				<div className="slip__event__competitors">
					{eventDetails.eventName}
				</div>
			);
		}

		return (
			<div className={teamClasses}>
				<TeamName
					name={eventDetails.teamOneName}
					gender={eventDetails.teamOneGender}
				/>{" "}
				-{" "}
				<TeamName
					name={eventDetails.teamTwoName}
					gender={eventDetails.teamTwoGender}
				/>
			</div>
		);
	}

	if (eventDetails.eventType !== EventType.Normal) {
		return (
			<LinkEventHOC
				eventId={eventId}
				isLive={isLive}
				className={classNames(teamClasses, "u-anchor")}
			>
				{eventDetails.eventName}
			</LinkEventHOC>
		);
	}

	return (
		<LinkEventHOC
			eventId={eventId}
			isLive={isLive}
			className={classNames(teamClasses, "u-anchor")}
		>
			<>
				<TeamName
					name={eventDetails.teamOneName}
					gender={eventDetails.teamOneGender}
				/>{" "}
				-{" "}
				<TeamName
					name={eventDetails.teamTwoName}
					gender={eventDetails.teamTwoGender}
				/>
			</>
		</LinkEventHOC>
	);
});

const LinkEventHOC = observer(function LinkEventHOC(props: {
	eventId: string;
	className?: string;
	isLive?: boolean;
	children: JSX.Element | JSX.Element[];
}) {
	const { eventId, className, isLive, children } = props;

	const page = isLive ? DefaultPath.LIVE : "sports/full/event";
	const url = `/${getCurrentCulture()}/${page}/${eventId}`;

	return (
		<Link className={className} to={url}>
			{children}
		</Link>
	);
});

const BankButton = observer(function BankButton(props: {
	store: BetSlipStore;
	eventId: string;
}) {
	if (props.store.betSlipType !== BetSlipTypes.SYSTEM) {
		return null;
	}

	return (
		<>
			<button
				type="button"
				onClick={() => props.store.addRemoveBank(props.eventId)}
				className={classNames(
					"slip__btn--outlined slip__btn--bank btn btn--square btn--square--xxsml",
					{
						"btn--cashout is-selected": props.store.isEventBank(
							props.eventId
						),
						"btn--primary--inverted u-color--text--secondary":
							!props.store.isEventBank(props.eventId),
					},
					"u-type--sml"
				)}
			>
				B
			</button>
		</>
	);
});

const RemoveEventOfferButton = function RemoveEventOfferButton(props: {
	store: BetSlipStore;
	bettingOfferId: string;
	isExcluded?: boolean;
}) {
	return (
		<ErrorBoundaryWithPropagation
			componentName={
				"Desktop/side-widget/bet-slip.tsx/RemoveEventOfferButton"
			}
		>
			<button
				type="button"
				onClick={() =>
					props.store.removeFromBetSlip({
						id: props.bettingOfferId,
					})
				}
				className="slip__btn--outlined slip__pick__remove btn btn--square btn--square--xxsml btn--primary--inverted btn--animate-grow"
			>
				<i
					className={
						"u-icon u-icon--tny u-icon--remove" +
						(props.isExcluded
							? " u-color--sent--muted"
							: " u-color--sent--danger")
					}
				/>
			</button>
		</ErrorBoundaryWithPropagation>
	);
};

const RemoveBetBuilderEventButton =
	function RemoveBetBuilderEventButton(props: {
		store: BetSlipStore;
		eventId: string;
		isExcluded?: boolean;
	}) {
		return (
			<ErrorBoundaryWithPropagation
				componentName={
					"Desktop/side-widget/bet-slip.tsx/RemoveBetBuilderEventButton"
				}
			>
				<button
					type="button"
					onClick={() =>
						// @ts-expect-error
						props.store.rootStore.betBuilderStore.removeBetBuilderEvent(
							props.eventId
						)
					}
					className="slip__btn--outlined slip__pick__remove btn btn--square btn--square--xxsml btn--primary--inverted btn--animate-grow"
				>
					<i
						className={
							"u-icon u-icon--tny u-icon--remove" +
							(props.isExcluded
								? " u-color--sent--muted"
								: " u-color--sent--danger")
						}
					/>
				</button>
			</ErrorBoundaryWithPropagation>
		);
	};

const RemoveBetBuilderOfferButton =
	function RemoveBetBuilderOfferButton(props: {
		store: BetSlipStore;
		offer: MultiOffer;
		isExcluded?: boolean;
	}) {
		return (
			<ErrorBoundaryWithPropagation
				componentName={
					"Desktop/side-widget/bet-slip.tsx/RemoveEventOfferButton"
				}
			>
				<>
					<button
						type="button"
						onClick={() => {
							// @ts-expect-error
							props.store.rootStore.betBuilderStore.updateQuotaAndSetAvailableOffers(
								{
									eventId: props.offer.eventId,
									id: props.offer.bettingOfferId,
								},
								// @ts-expect-error
								{ liveStatus: props.offer.isLive ? 1 : 0 }
							);
						}}
						className="slip__btn--ghost slip__pick__remove btn btn--square btn--square--xxsml btn--ghost btn--animate-grow"
					>
						<i
							className={
								"u-icon u-icon--tny u-icon--remove" +
								(props.isExcluded
									? " u-color--sent--muted"
									: " u-color--sent--danger")
							}
						/>
					</button>
				</>
			</ErrorBoundaryWithPropagation>
		);
	};


const Stakes = observer(function Stakes(props: { store: BetSlipStore }) {
	if (props.store.combinations > 1) {
		return (
			<React.Fragment>
				<div className="slip__item">
					<div className="slip__details__row">
						<StakePerCombination store={props.store} />
					</div>
				</div>
				<div className="slip__item">
					<div className="slip__details__row">
						<TotalStake store={props.store} />
					</div>
				</div>
			</React.Fragment>
		);
	}

	return (
		<div className="slip__item">
			<div className="slip__details__row">
				<TotalStake store={props.store} />
			</div>
		</div>
	);
});

const StakePerCombination = observer(function StakePerCombination(props: {
	store: BetSlipStore;
}) {
	const { t } = useTranslation();

    if (isUserAuthenticated() && props.store.betSlipState.freebetId) {
        return null;
    }

	return (
		<>
			<div className="slip__details__col">
				{t<string>("BET_SLIP.STAKE_PER_COMBINATION")}
			</div>
			<div className="slip__details__col">
				<div className="slip__field">
					<NumberInput
						disabled={props.store.isProcessingSlipEmpty}
						className="slip__field__input input input--sml"
						name="paymentPerCombination"
						value={props.store.paymentPerCombination}
						onChange={(newValue: number) => {
							props.store.onPaymentPerCombinationChange(newValue);
							props.store.onPaymentPerCombinationApply();
						}}
					/>
					<span className="slip__field__currency">
						<Currency />
					</span>
				</div>
			</div>
		</>
	);
});

const TotalStake = observer(function TotalStake(props: {
	store: BetSlipStore;
}) {
	const { t } = useTranslation();

	return (
		<ErrorBoundaryWithPropagation
			componentName={"Desktop/side-widget/bet-slip.tsx/TotalStake"}
		>
			<div className="slip__details__col">
                {t<string>("BET_SLIP.TOTAL_STAKE")}
			</div>
			<div className="slip__details__col">         
                {isUserAuthenticated() && props.store.betSlipState.freebetId ? (
                    <div className="btn__label u-color--sent--success">
                        {t<string>("BET_SLIP.FREEBET_STAKE")}
                        {(" - ")}
                        <strong>
                            <Number value={props.store.selectedFreebetAmount} /> <Currency />
                        </strong>
                    </div>
                ) : (
                    <>
                        <DefaultStakeButton store={props.store} />
                        <div className="slip__field">
                            <NumberInput
                                disabled={props.store.isProcessingSlipEmpty}
                                className="slip__field__input input input--sml"
                                name="totalStake"
                                value={props.store.payment}
                                onChange={(newValue: number) => {
                                    props.store.onTotalStakeChange(newValue);
                                    props.store.onTotalStakeApply();
                                }}
                            />
                            <Currency
                                className="slip__field__currency slip__field__currency--sml"
                                component="span"
                            />
                        </div>
                    </>
                )}
			</div>
		</ErrorBoundaryWithPropagation>
	);
});

const DefaultStakeButton = observer(function DefaultStakeButton(props: {
	store: BetSlipStore;
}) {
	const rootStore = useRootAppStore();

	if (rootStore.userAuthStore.user == null) {
		return null;
	}

	return (
		<button
			className="btn btn--square btn--square--xsml btn--primary--inverted u-mar--right--xtny"
			type="button"
			onClick={() => props.store.setDefaultBetSlipStake()}
		>
			<i className="u-icon u-icon--sml u-icon--set-stake" />
		</button>
	);
});

const SystemSection = observer(function SystemSection(props: {
	store: BetSlipStore;
}) {
	if (props.store.betSlipType !== BetSlipTypes.SYSTEM) {
		return null;
	}

	return (
		<div className="slip__system">
			<AddRemoveCombinations store={props.store} />
		</div>
	);
});

const AddRemoveCombinations = observer(function AddRemoveCombinations(props: {
	store: BetSlipStore;
}) {
	if (props.store.availableCombinations == null) {
		return null;
	}

	return (
		<>
			{props.store.availableCombinations.map((combo, i) => (
				<AvailableCombinationItem
					combo={combo}
					key={i}
					store={props.store}
				/>
			))}
		</>
	);
});

const AvailableCombinationItem = observer(
	function AvailableCombinationItem(props: {
		store: BetSlipStore;
		combo: {
			key: number;
			numberOfCombinations: number;
			total: number;
		};
	}) {
		return (
			<>
				<button
					onClick={(e) =>
						props.store.addRemoveCombination(props.combo.key)
					}
					className={classNames(
						"slip__system__btn btn btn--wrap btn--wrap--xxsml btn--ghost",
						{
							"is-selected": props.store.isCombinationSelected(
								props.combo.key
							),
						}
					)}
				>
					{props.store.bankOfferCount > 0 ? (
						<span className="u-type--wgt--medium">
							B{props.store.bankOfferCount} +{" "}
						</span>
					) : null}
					<>
						{props.combo.key}/{props.combo.total} (
						{props.combo.numberOfCombinations})
					</>
				</button>
			</>
		);
	}
);

const EventCount = observer(function EventCount(props: {
	store: BetSlipStore;
}) {
	const { t } = useTranslation();

	return (
		<>
			<div className="slip__details__row">
				<div className="slip__details__col">
					{t<string>("BET_SLIP.NO_PLAYED_EVENTS")}
				</div>
				<div className="slip__details__col">
					{props.store.eventCount}
				</div>
			</div>
		</>
	);
});

const Combinations = observer(function Combinations(props: {
	store: BetSlipStore;
}) {
	const { t } = useTranslation();

	if (props.store.combinations <= 1) {
		return null;
	}

	return (
		<div className="slip__item">
			<div className="slip__details__row">
				<div className="slip__details__col">
					{t<string>("BET_SLIP.NO_BETS")}
				</div>
				<div className="slip__details__col">
					{props.store.combinations}
				</div>
			</div>
		</div>
	);
});

const HandlingFee = observer(function HandlingFee(props: {
	store: BetSlipStore;
}) {
	const { t } = useTranslation();

	if (!props.store.isHandlingFeeActive || props.store.betSlipState.freebetId) {
		return null;
	}

	return (
		<div className="slip__item">
			<div className="slip__details__row">
				<div className="slip__details__col">
					{t<string>("BET_SLIP.HANDLING_FEE", {
						feePercentage: props.store.handlingFeePercentage,
					})}
				</div>
				<div className="slip__details__col">
					-<Number value={props.store.handlingFeeAmount} />
					<span className="u-mar--left--xtny">
						<Currency />
					</span>
				</div>
			</div>
		</div>
	);
});

const SlipMaxCoefficient = observer(function SlipMaxCoefficient(props: {
	store: BetSlipStore;
}) {
	const { t } = useTranslation();

	return (
		<div className="slip__item">
			<div className="slip__details__row">
				<div className="slip__details__col">
					{t<string>("BET_SLIP.MAX_COEF")}
				</div>
				<div className="slip__details__col">
					<Number value={props.store.slipMaxCoefficient} />
				</div>
			</div>
		</div>
	);
});

const GainLabel = observer(function GainLabel(props: { store: BetSlipStore }) {
	const { t } = useTranslation();

	if (
		props.store.gainBonusPercentage > 0 ||
		props.store.gainTaxPercentage > 0
	) {
		return (
			<div className="slip__details__col">
				{t<string>("BET_SLIP.GAIN")}
			</div>
		);
	}

	return (
		<div className="slip__details__col">
			{t<string>("BET_SLIP.MAX_GAIN")}
		</div>
	);
});

const Gain = observer(function Gain(props: { store: BetSlipStore }) {
	return (
        <div className="slip__details__col">
            {isUserAuthenticated() && props.store.betSlipState.freebetId ? (
                <div className="slip__details__col">
                    <Number value={props.store.gain} /> <Currency />
                </div>
            ) : (
                <div className="slip__field">
                    <NumberInput
                        disabled={props.store.isProcessingSlipEmpty}
                        className="slip__field__input input input--sml"
                        name="maxGain"
                        value={props.store.gain}
                        onChange={(newValue: number) => {
                            props.store.onGainChange(newValue);
                            props.store.onGainApply();
                        }}
                    />
                    <span className="slip__field__currency">
                        <span className="u-mar--left--xtny">
                            <Currency />
                        </span>
                    </span>
                </div>
            )}
        </div>
	);
});

const GainBonusPercentage = observer(function GainBonusPercentage(props: {
	store: BetSlipStore;
}) {
	const { t } = useTranslation();

	if (props.store.gainBonusPercentage <= 0) {
		return null;
	}

	return (
		<div className="slip__item">
			<div className="slip__details__row">
				<div className="slip__details__col">
					{" "}
					{t<string>("BET_SLIP.GAIN_BONUS")} (
					{props.store.gainBonusPercentage} %)
				</div>
				<div className="slip__details__col">
					+<Number value={props.store.gainBonusAmount} />
					<span className="u-mar--left--xtny">
						<Currency />
					</span>
				</div>
			</div>
		</div>
	);
});

const GainTaxPercentage = observer(function GainTaxPercentage(props: {
	store: BetSlipStore;
}) {
	const { t } = useTranslation();

	if (props.store.gainTaxPercentage <= 0 || props.store.betSlipState.freebetId) {
		return null;
	}

	return (
		<div className="slip__item">
			<div className="slip__details__row">
				<div className="slip__details__col">
					{" "}
					{t<string>("BET_SLIP.GAIN_TAX")} (
					{props.store.gainTaxPercentage} %)
				</div>
				<div className="slip__details__col">
					-<Number value={props.store.gainTaxAmount} />
					<span className="u-mar--left--xtny">
						<Currency />
					</span>
				</div>
			</div>
		</div>
	);
});

const MaxGain = observer(function MaxGain(props: { store: BetSlipStore }) {
	const { t } = useTranslation();

	if (
		props.store.gainBonusPercentage <= 0 &&
		props.store.gainTaxPercentage <= 0
	) {
		return null;
	}

	return (
		<div className="slip__item">
			<div className="slip__details__row">
				<div className="slip__details__col">
					{t<string>("BET_SLIP.MAX_GAIN")}
				</div>
				<div className="slip__details__col">
					<Number value={props.store.maxGain} />
					<span className="u-mar--left--xtny">
						<Currency />
					</span>
				</div>
			</div>
		</div>
	);
});

const Payment = observer(function Payment(props: { store: BetSlipStore }) {
	const { t } = useTranslation();

	return (
		<div className="slip__item">
			<div className="slip__details__row u-type--wgt--medium">
				<div className="slip__details__col">
					{t<string>("BET_SLIP.PAYMENT")}
				</div>
				<div className="slip__details__col">
					<Number value={props.store.payment} />
					<span className="u-mar--left--xtny">
						<Currency />
					</span>
				</div>
			</div>
		</div>
	);
});

const CheckOdds = observer(function CheckOdds(props: { store: BetSlipStore }) {
	const { t } = useTranslation();

	return (
		<ErrorBoundaryWithPropagation
			componentName={"Desktop/side-widget/bet-slip.tsx/CheckOdds"}
		>
			<div className="slip__settings">
				<div className="slip__acceptance input--check input--check--sml">
					<input
						id="acceptOdds"
						type="checkbox"
						checked={props.store.acceptOddsChanges}
						onChange={() =>
							props.store.onToggleAcceptOddsChangesChange()
						}
						className="input--check__box input--check--sml__box"
					/>
					<label
						htmlFor="acceptOdds"
						className="slip__acceptance__label input--check__label input--check--sml__label"
					>
						{t<string>("BET_SLIP.TYPE.CHECK_ODDS")}
					</label>
				</div>

				<Button
					type="button"
					onClick={props.store.startBetSlipReset}
					className="btn--square btn--square--xxsml btn--ghost btn--icon slip__btn--reset"
					iconClassName="u-icon u-icon--sml u-icon--bin--str u-color--sent--danger"
					iconOnly
					title={t<string>("BET_SLIP.REMOVE_ALL_BETS")}
				/>
			</div>
		</ErrorBoundaryWithPropagation>
	);
});

const SlipFooter = observer(function SlipFooter(props: {
	store: BetSlipStore;
}) {
	const rootStore = useRootAppStore();

	if (rootStore.userAuthStore.isLoggedIn) {
		return <SlipFooterAuthenticated store={props.store} />;
	}

	return <SlipFooterNotAuthenticated />;
});

const RemoveInvalidOfferButton = observer(function RemoveInvalidOfferButton() {
	const { t } = useTranslation();
	const rootStore = useRootAppStore();

	if (!rootStore.betSlipStore.hasInvalidBets) {
		return null;
	}

	return (
		<ErrorBoundaryWithPropagation
			componentName={
				"Desktop/side-widget/bet-slip.tsx/RemoveInvalidOfferButton"
			}
		>
			<button
				type="button"
				onClick={rootStore.betSlipStore.removeInactiveBets}
				className="slip__btn--outlined slip__btn--remove btn btn--sml btn--block btn--primary--inverted"
			>
				{t<string>("BET_SLIP.REMOVE_INACTIVE")}
			</button>
		</ErrorBoundaryWithPropagation>
	);
});

const SlipFooterAuthenticated = observer(
	function SlipFooterAuthenticated(props: { store: BetSlipStore }) {
		const { t } = useTranslation();

		const submitBtnClasses = classNames(
			"slip__button btn btn--base btn--block btn--accent",
			{
				"slip--builder__submit":
					props.store.betSlipState.isMultiOfferPresent,
				"slip__button--sharp": props.store.showConfirmBetSlipSubmit,
				"btn--animate-forward": !props.store.showConfirmBetSlipSubmit,
				"btn--animate-grow": props.store.showConfirmBetSlipSubmit,
			}
		);

		const submitIconClasses = classNames("u-icon--sml", {
			"u-icon--arrow-right u-color--accent--text":
				!props.store.showConfirmBetSlipSubmit,
			"u-icon--check u-color--sent--success":
				props.store.showConfirmBetSlipSubmit,
		});

		return (
			<>
                <Button
                    type="button"
                    disabled={
                        props.store.isSubmitDisabled ||
                        App.state.rootStore.waitingPeriodStore.timeLeft > 0
                    }
                    onClick={(e) => {
                        if (props.store.showConfirmBetSlipSubmit) {
                            props.store.onSubmitSlip();
                        } else {
                            props.store.startBetSlipSubmitProcess();
                        }
                    }}
                    btnText={t<string>("BET_SLIP.SUBMIT_BET")}
                    className={submitBtnClasses}
                    iconLeft={props.store.showConfirmBetSlipSubmit}
                    iconRight={!props.store.showConfirmBetSlipSubmit}
                    iconClassName={submitIconClasses}
                />
				<BetSlipSubmitConfirmation store={props.store} />
			</>
		);
	}
);

const FreebetSlipSubmit = observer(function FreebetSlipSubmit() {
    const { t } = useTranslation();
    const rootStore = useRootAppStore();

    const isLoggedIn = rootStore.userAuthStore.isLoggedIn

    useEffect(() => {
        if (isLoggedIn) {
            rootStore.betSlipStore.fetchFreeBets();
        }
    }, [isLoggedIn]);

    if (!rootStore.betSlipStore.shouldShowFreebetButton) {
        return null;
    }

    return (
        <div className="slip__item u-mar--bottom--med">

            {rootStore.betSlipStore.betSlipState.freebetId ? (
                <RemoveFreebet />
            ) : (
                    <Button
                        type="button"
                        disabled={
                            rootStore.betSlipStore.isSubmitDisabled ||
                            rootStore.waitingPeriodStore.timeLeft > 0
                        }
                        onClick={rootStore.betSlipStore.openFreebetSelector}
                        className="slip__btn slip__btn--reset btn btn--med btn--block btn--success--inverted btn--icon u-type--wgt--bold"
                        iconClassName="u-icon u-icon--med u-icon--my-bonuses--bold u-color--sent--success u-mar--bottom--tny"
                        iconLeft
                        btnText={
                            rootStore.betSlipStore.availableFreeBets.length === 1
                                ? t("BET_SLIP.AVAILABLE_FREE_BET", {
                                    numberOfFreebets: rootStore.betSlipStore.availableFreeBets.length,
                                })
                                : t("BET_SLIP.NUM_OF_AVAILABLE_FREE_BETS", {
                                    numberOfFreebets: rootStore.betSlipStore.availableFreeBets.length,
                                })
                        }
                    />
            )}

            {rootStore.betSlipStore.isFreebetModalOpen && (
                <Modal>
                    <div className="popup">
                        <div className="popup__card">

                            <div className="popup__header">
                                <div>
                                    <div className="popup__title">
                                        {t("BET_SLIP.AVAILABLE_FREE_BETS")}
                                    </div>
                                    <div className="label u-type--xxsml">
                                        {t("BET_SLIP.CHECK_FREE_BET_OPTIONS")}
                                    </div>
                                </div>
                            </div>

                            <div className="popup__body">
                                {rootStore.betSlipStore.availableFreeBets.map(freebet => (
                                    <FreebetItem
                                        key={freebet.id}
                                        freebet={freebet}
                                        isSelected={rootStore.betSlipStore.draftFreebetId === freebet.id}
                                        onSelect={rootStore.betSlipStore.selectDraftFreebet}
                                    />
                                ))}
                            </div>

                            <div className="popup__footer u-display--f--justify-spaced">
                                <p className="u-display--f u-display--f--center"> {t("BET_SLIP.FREEBET_T&C_INFO")} <a className="u-mar--left--xtny u-anchor u-anchor--underline u-anchor--primary popup--search__thumbs u-mar--top--xtny" href="/info/freebet-terms" target="_blank">{t("BET_SLIP.FREEBET_T&C_LINK")} <i className="u-icon--video-unfloat u-icon--xmed u-mar--bottom--2" /> </a></p>
                                <div className="u-display--f u-gap--base">
                                    <Button
                                        type="button"
                                        className="popup__btn btn btn--med btn--primary--inverted"
                                        onClick={rootStore.betSlipStore.closeFreebetModal}
                                        btnText={t("COMMON.CLOSE")}
                                    />

                                    <Button
                                        type="button"
                                        className="popup__btn btn btn--med btn--accent"
                                        disabled={!rootStore.betSlipStore.draftFreebetId}
                                        onClick={rootStore.betSlipStore.applyFreebet}
                                        btnText={t("BET_SLIP.APPLY_FREE_BET")}
                                    />
                                </div>
                            </div>

                        </div>
                    </div>
                </Modal>
            )}
        </div>
    );
}
);

const RemoveFreebet = observer(
    function RemoveFreebet() {
        const { t } = useTranslation();
        const rootStore = useRootAppStore();

        return (
            <>
                <Button
                    type="button"
                    className="slip__btn slip__btn--reset btn btn--med btn--block btn--danger--inverted btn--icon"
                    onClick={() => {
                        rootStore.betSlipStore.removeFreebet();
                        rootStore.betSlipStore.closeFreebetModal();
                    }}
                    iconClassName="u-icon u-icon--sml u-icon--remove u-color--sent--danger"
                    iconLeft
                    btnContent={
                        <div className="slip__button__content">
                            <span className="slip__button__text u-type--wgt--medium">
                                {t<string>("BET_SLIP.REMOVE_FREE_BET")}
                            </span>
                            <span className="slip__button__text u-type--tny">
                                {t<string>("BET_SLIP.BACK_TO_REGULAR_BET_SLIP")}
                            </span>
                        </div>
                    }
                />

                <FreebetTermsInfo />
            </>
        );
    }
);

const FreebetItem = observer(function FreebetItem(props: { freebet: Freebet, isSelected: boolean, onSelect: (freebet: Freebet) => void }) {
    const { t } = useTranslation();

    const formattedExpiration = useMemo(() => {
        return DateTime.fromISO(props.freebet.availableUntil, { zone: "utc" })
            .toLocal()
            .toLocaleString(DateTime.DATETIME_SHORT);
    }, [props.freebet.availableUntil]);

    return (
        <ErrorBoundaryWithPropagation
            componentName={"Desktop/side-widget/bet-slip.tsx/FreebetItem"}
        >
            <div className="slip__event">
                <div className="slip__acceptance input--check input--check--lrg">
                    <input
                        id="freebetOption"
                        type="checkbox"
                        checked={props.isSelected}
                        onChange={() => {
                            props.onSelect(props.freebet);
                        }}
                        className="input--radio__box input--radio--med__box u--mar--top--xtny"
                    />
                    <div className="slip--freebet">
                        {t<string>("BET_SLIP.FREEBET_STAKE")}
                        {" - "}
                        <Number value={props.freebet.stakeAmount} />
                        <span className="u-mar--left--xtny">
                            <Currency />
                        </span>
                    </div>
                    <div className="u-type--xxsml u-color--text--secondary">
                        {t<string>("BET_SLIP.FREE_BET_EXPIRATION", {
                            expirationDate: formattedExpiration,
                            interpolation: { escapeValue: false },
                        })}
                    </div>
                </div>
            </div>
        </ErrorBoundaryWithPropagation>
    );
});

const FreebetTermsInfo = observer(function FreebetTermsInfo() {

    const { t } = useTranslation();
	const rootStore = useRootAppStore();

    if(!rootStore.betSlipStore.betSlipState.freebetId) {
        return null;
    }

    return (
        <div className="u-color--text--secondary u-mar--top--3 u-display--f"> 
            <i className="u-icon--info-circle u-icon--sml u-mar--right--2 u-mar--top--1" />
                {t("BET_SLIP.FREEBET_T&C")}
        </div>                
    )
})


const BetSlipSubmitConfirmation = observer(
	function BetSlipSubmitConfirmation(props: { store: BetSlipStore }) {
		const { t } = useTranslation();

		if (!props.store.showConfirmBetSlipSubmit) {
			return null;
		}

		return (
			<button
				className="slip__button btn btn--base btn--block btn--ghost btn--icon btn--animate-grow"
				type="button"
				onClick={() => props.store.cancelBetSlipSubmit()}
			>
				<i className="u-icon u-icon--sml u-icon--remove u-color--sent--danger" />
				<span className="slip__button__text">
					{t<string>("COMMON.CANCEL")}
				</span>
			</button>
		);
	}
);

const SlipFooterNotAuthenticated = function SlipFooterNotAuthenticated() {
	const { t } = useTranslation();

	return (
		<Button
			onClick={() => redirectToLogin()}
			className="slip__button btn btn--base btn--block btn--accent btn--icon"
			btnContent={t("BET_SLIP.LOGIN_TO_SUBMIT")}
			iconClassName="u-icon u-icon--med u-icon--login u-color--accent--text"
			iconRight
		/>
	);
};

const SaveSlipButton = observer(function SaveSlipButton(props: {
	store: BetSlipStore;
}) {
	const { t } = useTranslation();

	const rootStore = useRootAppStore();

	if (!WEBPACK_IS_READY_BET_ENABLED) {
		return null;
	}

	if(!rootStore.userAuthStore.isLoggedIn && !THEME_SETTINGS.isReadyBetRetailEnabled){
		return null;
	}

    if (props.store.betSlipState.freebetId) {
        return null;
    }

	return (
		<Button
			onClick={() => {
				props.store.savedSlipStore.savePreparedSlip(
					SavedSlipInfoType.POPUP
				);
			}}
			className="btn btn--base btn--block btn--icon btn--primary--inverted"
			btnContent={t("BET_SLIP.BET_SLIP_SAVED.SAVE_SLIP")}
			iconClassName="slip__btn__icon u-icon u-icon--sml u-icon--bets--reg u-color--text--secondary"
			iconLeft
		/>
	);
});
